import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import RHFTextArea from "@/components/form/RHFTextArea";
import { RotateCcw, X } from "lucide-react";

import { getStatusLabel } from "../constants/actions";
import { UNDO_REASON_MAX_LENGTH, undoStatusReasonDefaultValues, undoStatusReasonSchema } from "../validation.schema";

/**
 * Asking for a Hired or Rejected candidate to be reopened.
 *
 * The candidate does not move when this is submitted: a manager or an admin
 * decides, and the application's status is locked until they do. So the modal
 * says what is being asked and of whom, and makes the recruiter write the
 * reason the approver will read. The reason follows the same rule as a direct
 * undo's (`undoStatusReasonSchema`), so the one question "why is this status
 * being walked back" has one set of bounds.
 *
 * `targetStatus` is the stage the server says the candidate would return to.
 */
const UndoRequestModal = ({ isOpen, onClose, onSubmit, candidateName, currentStatus, targetStatus, isLoading }) => {
  const methods = useForm({
    resolver: zodResolver(undoStatusReasonSchema),
    mode: "onChange",
    defaultValues: undoStatusReasonDefaultValues,
  });
  const { handleSubmit } = methods;

  const currentLabel = getStatusLabel(currentStatus, "their current status");
  const targetLabel = getStatusLabel(targetStatus, "their previous stage");

  const dismiss = () => {
    if (isLoading) return;
    onClose();
  };

  // `reason` arrives normalized: the schema transforms before it validates.
  const submit = ({ reason }) => onSubmit(reason);

  return (
    <AlertDialog open={!!isOpen} onOpenChange={(open) => !open && dismiss()}>
      <AlertDialogContent className="!max-w-2xl" overlayClassName="supports-backdrop-filter:backdrop-blur-md" onOverlayClick={dismiss}>
        <ButtonShadcn
          variant="ghost"
          size="icon"
          onClick={dismiss}
          disabled={isLoading}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-background-tertiary text-text-secondary transition-colors hover:bg-background-hover hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={16} aria-hidden="true" />
        </ButtonShadcn>

        <AlertDialogHeader>
          <AlertDialogTitle className="border-b border-border-primary pb-1 pr-8 text-lg">Request to reopen this candidate</AlertDialogTitle>
          <AlertDialogDescription className="pr-8">
            This candidate is currently marked as <span className="font-semibold text-text-primary">{currentLabel}</span>. Reopening the candidate requires approval.
            Please provide a reason for requesting this status change.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <p className="m-0 text-sm text-text-secondary">
          If approved, <span className="font-semibold text-text-primary">{candidateName || "this candidate"}</span> returns to{" "}
          <span className="font-semibold text-text-primary">{targetLabel}</span>. Until it is decided, nobody can change this candidate&apos;s status.
        </p>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submit)}>
            <RHFTextArea
              name="reason"
              id="undo-request-reason"
              label="Reason"
              placeholder="Why should this candidate be reopened?"
              required
              rows={4}
              maxLength={UNDO_REASON_MAX_LENGTH}
              counter="near-limit"
              disabled={isLoading}
              className="mb-4"
              inputClass="resize-y min-h-24 max-h-48 overflow-y-auto"
            />

            <AlertDialogFooter>
              <AlertDialogCancel onClick={onClose} disabled={isLoading}>
                Cancel
              </AlertDialogCancel>
              <Button
                type="submit"
                disabled={isLoading}
                className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading && <RotateCcw size={14} className="animate-spin" aria-hidden="true" />}
                {isLoading ? "Submitting…" : "Submit Undo Request"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UndoRequestModal;
